import React, { useCallback, useRef, useState } from 'react';

import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ListItem,
  ListItemText,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { usePhoneInput } from 'react-international-phone';

import { ContactAvatar } from 'components/ContactAvatar';
import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import NewMessagesContainer from 'components/NewMessagesContainer';
import TagsV2 from 'components/TagsV2/TagsV2';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import {
  type AtendimentoInterfaceV2,
  setHistoricoAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import {
  comentariosSelector,
  fetchComentarios,
  removerComentario,
  resetComentarios,
} from 'store/reducers/comentarios';
import { ContatoInterfaceV2 } from 'store/reducers/contatos';
import {
  fetchMensagensFinalizadasByAtendimento,
  mensagensSelector,
  resetHistoricoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensFinalizadas,
  resetSearchQuery,
  setAfterCursorAConsumir,
  setBeforeCursorAConsumir,
  setCursorAConsumir,
} from 'store/reducers/mensagens';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';
import { normalizeDuration } from 'utils/normalizeDuration';

import DetailItem from './DetailItems';

const useStyles = makeStyles(({ palette, spacing }) => ({
  dialogPaper: {
    height: '90%',
    width: '80%',
    overflow: 'hidden',
  },

  dialogContent: {
    padding: 0,
    margin: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:first-child': {
      paddingTop: 0,
    },
  },

  gridContainer: {
    height: '100%',
    width: '100%',
    margin: 0,
  },

  leftColumn: {
    height: '100%',
    overflowY: 'auto',
    padding: spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(2),
    borderRight: `1px solid ${palette.divider}`,
    position: 'relative',
  },
  contactInfo: {
    width: '100%',
    padding: spacing(1),
    backgroundColor: alpha(palette.background.tertiary, 0.5),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    borderRadius: spacing(1),
  },

  mainGrid: {
    height: '100%',
    padding: 0,
    margin: 0,
    backgroundColor: alpha(palette.background.tertiary, 0.5),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    width: '100%',
    overflow: 'hidden',
    padding: 8,
    backgroundColor: palette.background.tertiary,
    '& > .MuiCardHeader-root': {
      paddingBottom: 0,
    },
    '& > .MuiCardContent-root': {
      padding: spacing(1, 2),
      paddingBottom: spacing(2),
      wordBreak: 'break-word',
    },
  },
  list: {
    height: '100%',
    position: 'relative',
    overflow: 'hidden auto',
  },

  tagsWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 4,
  },

  closeButton: {
    position: 'absolute',
    top: spacing(1),
    right: spacing(1),
    backgroundColor: palette.background.paper,
    zIndex: 2,

    '&:hover': {
      backgroundColor: palette.background.tertiary,
    },
  },
  tab: {
    borderTop: `1px solid ${palette.divider}`,
    '& .MuiTab-root': {
      minWidth: 'auto',
    },
  },
  tabPanelHistorico: {
    height: '100%',
    padding: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  stickySelect: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: palette.background.paper,
    borderBottom: `1px solid ${alpha(palette.divider, 0.1)}`,
    padding: spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
  },
}));

interface ModalHistoricoAtendimentoContatoProps {
  atendimento: AtendimentoInterfaceV2;
  isAtendimentoEmAndamento?: boolean;
  open: boolean;
  onClose: () => void;
}

export default function ModalHistoricoAtendimentoContato({
  atendimento,
  isAtendimentoEmAndamento = false,
  open,
  onClose,
}: ModalHistoricoAtendimentoContatoProps) {
  const classes = useStyles();
  const [initialLoadingMessages, setInitialLoadingMessages] = useState(false);
  const contato = atendimento.contact as ContatoInterfaceV2;
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();

  const {
    historicoCursorAConsumir,
    mensagensFinalizadas,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
    loadingMensagensFinalizadas,
  } = useAppSelector(mensagensSelector);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    comentarios,
    comentariosCursor,
    loadingComentarios,
    loadingRemoverComentario,
  } = useAppSelector(comentariosSelector);

  const { phone } = usePhoneInput({
    defaultCountry: 'br',
    value: contato?.contact ?? '',
  });

  const onfocus = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  const handleConfirmRemove = (comentarioId: string) => {
    confirm({
      title: <Typography variant="body2">Remover comentário</Typography>,
      description: (
        <Typography variant="caption">
          Deseja mesmo remover este comentário permanentemente?
        </Typography>
      ),
      cancellationText: 'Cancelar',
      confirmationText: 'Remover',
      confirmationButtonProps: { variant: 'contained', color: 'secondary' },
      dialogProps: { maxWidth: 'xs' },
    })
      .then(async () => {
        const atendimentoId = atendimento?.id;
        if (atendimentoId) {
          /**
           * Remove um comentario
           * Limpa e atualiza a lista de comentarios após a remoção.
           */
          await dispatch(removerComentario({ atendimentoId, comentarioId }))
            .unwrap()
            .then(() => {
              onfocus();

              dispatch(resetComentarios());
              dispatch(fetchComentarios({ atendimentoId }));
            })
            .catch(error => {
              if (error.response) enqueueSnackbar(error.response.data.message);
            });
        }
      })
      .catch(() => {
        //
      });
  };

  const fetchNextMensagensFinalizadas = useCallback(() => {
    const { afterCursor: afterAConsumir } = historicoCursorAConsumir[0] || {};
    const { afterCursor } = mensagensFinalizadasCursor;

    const atendimentoId = atendimento?.id;

    if (!atendimento) return;

    if (!mensagemIdPesquisada) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { afterCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { afterCursor } = data.cursor;

          dispatch(setAfterCursorAConsumir({ afterCursor, type: 'historico' }));
        });

      return;
    }

    if (afterAConsumir) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { afterCursor: afterAConsumir },
        }),
      )
        .unwrap()
        .then(data => {
          const { afterCursor: currentAfterCursor } = data.cursor;

          dispatch(
            setAfterCursorAConsumir({
              afterCursor: currentAfterCursor,
              type: 'historico',
            }),
          );
        });
    }
  }, [
    atendimento,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
  ]);

  const fetchPreviousMensagensFinalizadas = useCallback(() => {
    const { beforeCursor: beforeAConsumir } = historicoCursorAConsumir[0] || {};
    const { beforeCursor } = mensagensFinalizadasCursor;

    const atendimentoId = atendimento?.id;

    if (!atendimentoId) return;

    if (!mensagemIdPesquisada) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { beforeCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { beforeCursor } = data.cursor;

          dispatch(
            setBeforeCursorAConsumir({ beforeCursor, type: 'historico' }),
          );
        });

      return;
    }

    if (beforeAConsumir) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { beforeCursor: beforeAConsumir },
        }),
      )
        .unwrap()
        .then(data => {
          const { beforeCursor: currentBeforeCursor } = data.cursor;

          dispatch(
            setBeforeCursorAConsumir({
              beforeCursor: currentBeforeCursor,
              type: 'historico',
            }),
          );
        });
    }
  }, [
    atendimento?.id,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
  ]);

  const onLoadNextComentarios = () => {
    const { afterCursor } = comentariosCursor;
    if (atendimento?.id && afterCursor) {
      dispatch(
        fetchComentarios({
          atendimentoId: atendimento?.id,
          cursor: { afterCursor },
        }),
      );
    }
  };

  const handleResetLista = useCallback(() => {
    const atendimentoId = atendimento?.id;

    if (atendimentoId) {
      setInitialLoadingMessages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetHistoricoCursorAConsumir());
      dispatch(resetMensagensFinalizadas());
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
        })
        .finally(() => {
          setInitialLoadingMessages(false);
        });
    }
  }, [atendimento?.id, dispatch]);

  const displayPhone = contato?.contact && phone ? phone : contato?.name;
  const IconTipoCanal = contato
    ? getIconTipoCanal({
        tipoCanal: contato?.tipoCanal,
        size: 16,
        isModoHibrido:
          contato.channel?.isHybridMode || atendimento?.channel?.isHybridMode,
      })
    : null;

  const renderComentarios = comentarios.map(comentario => {
    const { id, usuario, criadoEm, texto } = comentario;
    const fromMe = currentUser?.id === usuario.id;
    const autor = fromMe ? 'Você' : getUsuarioNomeExibicao(usuario);

    return (
      <ListItem key={id} disableGutters>
        <Card className={classes.card} elevation={0}>
          <CardHeader
            title={autor}
            style={{ padding: 0 }}
            titleTypographyProps={{ noWrap: true, variant: 'caption' }}
            subheader={dayjs(criadoEm).fromNow()}
            subheaderTypographyProps={{ variant: 'caption' }}
            avatar={
              <ImageViewer src={usuario.foto}>
                <ContactAvatar src={usuario.foto} />
              </ImageViewer>
            }
            action={
              fromMe &&
              isAtendimentoEmAndamento && (
                <IconButton
                  aria-label="remove"
                  onClick={() => handleConfirmRemove(id)}
                  disabled={loadingRemoverComentario}
                >
                  {loadingRemoverComentario ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <Close fontSize="small" />
                  )}
                </IconButton>
              )
            }
          />

          <CardContent style={{ padding: 0 }}>
            <Typography variant="caption" color="textSecondary" component="p">
              {texto}
            </Typography>
          </CardContent>
        </Card>
      </ListItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      TransitionProps={{
        onEnter: () => {
          if (atendimento) {
            dispatch(fetchComentarios({ atendimentoId: atendimento?.id }));
            dispatch(setHistoricoAtendimentoSelecionado(atendimento));
            handleResetLista();
          }
        },
        onExited: () => {
          dispatch(resetComentarios());
          dispatch(setHistoricoAtendimentoSelecionado(undefined));
          dispatch(resetSearchQuery());
        },
      }}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogContent className={classes.dialogContent}>
        <Box
          display="flex"
          flexDirection={isSmallScreen ? 'column' : 'row'}
          height="100%"
        >
          <IconButton onClick={onClose} className={classes.closeButton}>
            <Close />
          </IconButton>

          <Box
            style={{
              width: isSmallScreen ? '100%' : '25%',
              display: 'flex',
              flexDirection: 'column',
              height: isSmallScreen ? 'auto' : '100%',
              borderRight: isSmallScreen
                ? 'none'
                : `1px solid ${theme.palette.divider}`,
            }}
          >
            {isSmallScreen && (
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant="fullWidth"
                centered
                className={classes.tab}
              >
                <Tab label="Informações" />
                <Tab label="Histórico" />
              </Tabs>
            )}

            {(!isSmallScreen || tabValue === 0) && (
              <Box className={classes.leftColumn} flex={1} id="leftColumn">
                <Box width="100%" display="flex" justifyContent="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    className={classes.contactInfo}
                  >
                    <ImageViewer src={contato?.fotoUrl}>
                      <ContactAvatar src={contato?.fotoUrl} />
                    </ImageViewer>

                    <ListItemText
                      primary={contato?.name}
                      secondary={
                        <Box
                          display="flex"
                          alignItems="center"
                          gridGap={8}
                          component="span"
                        >
                          {IconTipoCanal}
                          <Box component="span">{displayPhone}</Box>
                        </Box>
                      }
                      primaryTypographyProps={{
                        style: { fontSize: 14, fontWeight: 500 },
                        component: 'div',
                      }}
                      secondaryTypographyProps={{
                        component: 'div',
                        style: { fontSize: 12 },
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <DetailItem
                    marginBottom={8}
                    label="Código"
                    value={`${atendimento.code}`}
                  />
                  <DetailItem
                    marginBottom={8}
                    label="Iniciado em"
                    value={dayjs(atendimento.startedIn).format(
                      'DD/MM/YY[, às] HH:mm',
                    )}
                  />
                  {atendimento.finalizadoPor && (
                    <DetailItem
                      marginBottom={8}
                      label="Finalizado em"
                      value={dayjs(atendimento.finishedIn).format(
                        'DD/MM/YY[, às] HH:mm',
                      )}
                    />
                  )}

                  {atendimento.durationInSeconds && (
                    <DetailItem
                      marginBottom={8}
                      label="Duração"
                      value={normalizeDuration(atendimento.durationInSeconds)}
                    />
                  )}

                  {!atendimento.finishedIn && (
                    <DetailItem
                      marginBottom={8}
                      label="Atendente"
                      value={atendimento.attendantCurrent?.name}
                    />
                  )}

                  {atendimento.finishedIn && (
                    <DetailItem
                      marginBottom={8}
                      label="Finalizado por"
                      value={atendimento.attendantFinisher?.name}
                    />
                  )}

                  <DetailItem
                    marginBottom={8}
                    label="Equipe"
                    value={atendimento.team?.name}
                  />
                  <DetailItem
                    marginBottom={8}
                    label="Canal"
                    value={atendimento.channel?.name}
                  />
                  <DetailItem
                    layout="row"
                    marginBottom={8}
                    label="Tags"
                    className={classes.tagsWrapper}
                    value={
                      <TagsV2
                        tags={
                          atendimento.tags?.map(t => ({
                            id: t.id,
                            nome: t.name,
                            cor: t.color,
                          })) || []
                        }
                        id={atendimento.id}
                      />
                    }
                  />
                </Box>
                <DetailItem
                  fullWidth
                  marginBottom={8}
                  label="Comentários"
                  value={
                    <InfiniteScroll
                      data={comentarios}
                      next={onLoadNextComentarios}
                      hasMore={Boolean(comentariosCursor.afterCursor)}
                      loading={loadingComentarios}
                      scrollableTarget="leftColumn"
                    >
                      {loadingComentarios && comentarios.length === 0 && (
                        <CircularProgress size={20} />
                      )}

                      {!loadingComentarios && comentarios.length === 0 && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height={80}
                        >
                          <Typography variant="body2" color="textSecondary">
                            Sem comentários
                          </Typography>
                        </Box>
                      )}

                      {renderComentarios}
                    </InfiniteScroll>
                  }
                />
              </Box>
            )}
          </Box>

          {(!isSmallScreen || tabValue === 1) && (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              height={isSmallScreen ? '100%' : '100%'}
              className={classes.mainGrid}
              style={{ minHeight: 0 }}
            >
              <Box
                flex={1}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                {!initialLoadingMessages && (
                  <NewMessagesContainer
                    messages={mensagensFinalizadas}
                    next={fetchNextMensagensFinalizadas}
                    previous={fetchPreviousMensagensFinalizadas}
                    hasAfterCursor={
                      mensagemIdPesquisada
                        ? Boolean(historicoCursorAConsumir[0]?.afterCursor)
                        : Boolean(mensagensFinalizadasCursor.afterCursor)
                    }
                    hasBeforeCursor={
                      mensagemIdPesquisada
                        ? Boolean(historicoCursorAConsumir[0]?.beforeCursor)
                        : Boolean(mensagensFinalizadasCursor.beforeCursor)
                    }
                    onResetLista={handleResetLista}
                    loading={loadingMensagensFinalizadas}
                    scrollableTargetId="tabPanelConversa"
                    actions={false}
                    endMessage={`Este é o início da sua conversa com ${contato.name}`}
                    historicoAtendimento
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
